import React, { useEffect, useState, useCallback } from "react";
import {
  Calendar, CheckCircle, XCircle, Clock, TrendingUp,
  Download, BookOpen, ChevronLeft, ChevronRight, RefreshCw,
} from "lucide-react";
import { lmsGetManualAttendance } from "../api/lmsApi";
import "../lms.css";

const STATUS_CFG = {
  present: { label: "Present", color: "#16a34a", bg: "#dcfce7", border: "#bbf7d0", icon: CheckCircle },
  absent:  { label: "Absent",  color: "#dc2626", bg: "#fee2e2", border: "#fecaca", icon: XCircle },
  late:    { label: "Late",    color: "#d97706", bg: "#fef3c7", border: "#fde68a", icon: Clock },
};

function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function dayOfWeek(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", { weekday: "short" });
}

export default function StudentAttendance() {
  const userData  = (() => { try { return JSON.parse(localStorage.getItem("userData") || "{}"); } catch { return {}; } })();
  const studentId = userData?.student_id || userData?.id;
  const schoolId  = userData?.school_id  || userData?.admin_id || null;

  const [records,     setRecords]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [filterMonth, setFilterMonth] = useState("");

  const fetchRecords = useCallback(async () => {
    if (!studentId) { setError("Student ID not found. Please re-login."); setLoading(false); return; }
    setLoading(true);
    setError("");
    try {
      const params = { student_id: studentId };
      if (schoolId) params.school_id = schoolId;
      const res  = await lmsGetManualAttendance(params);
      const data = res?.data?.data || res?.data || [];
      setRecords(Array.isArray(data) ? data : []);
    } catch {
      setError("Failed to load attendance records. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [studentId, schoolId]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // Month list from records
  const months = [...new Set(records.map((r) => r.date?.slice(0, 7)).filter(Boolean))].sort((a, b) => b.localeCompare(a));

  const filtered = filterMonth ? records.filter((r) => r.date?.startsWith(filterMonth)) : records;

  const total   = filtered.length;
  const present = filtered.filter((r) => r.status === "present").length;
  const absent  = filtered.filter((r) => r.status === "absent").length;
  const late    = filtered.filter((r) => r.status === "late").length;
  const pct     = total ? Math.round((present / total) * 100) : 0;
  const pctColor = pct >= 75 ? "#16a34a" : pct >= 50 ? "#d97706" : "#dc2626";
  const pctBg    = pct >= 75 ? "#dcfce7" : pct >= 50 ? "#fef3c7" : "#fee2e2";

  const exportCSV = () => {
    const header = "Date,Day,Class,Subject,Status\n";
    const rows   = filtered.map((r) =>
      `${r.date || ""},${dayOfWeek(r.date)},${r.class_name || ""},${r.subject_name || ""},${r.status || ""}`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "my_attendance.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // Month navigation
  const currentMonthIdx = months.indexOf(filterMonth);
  const goPrev = () => { if (currentMonthIdx < months.length - 1) setFilterMonth(months[currentMonthIdx + 1]); };
  const goNext = () => { if (currentMonthIdx > 0) setFilterMonth(months[currentMonthIdx - 1]); else setFilterMonth(""); };

  const monthLabel = filterMonth
    ? new Date(filterMonth + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })
    : "All Time";

  return (
    <div className="lms-content-inner">

      {/* ── Page Header ── */}
      <div style={{
        background: "#fff", borderRadius: 14, marginBottom: 20,
        border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#06b6d4,#16a34a)" }} />
        <div style={{ padding: "18px 22px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          {/* Icon + Title */}
          <div style={{
            width: 46, height: 46, borderRadius: 12, flexShrink: 0,
            background: "linear-gradient(135deg,#4f46e5,#06b6d4)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Calendar size={22} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: "#111827" }}>My Attendance</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>Track your class presence across all subjects</div>
          </div>

          {/* Month nav */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button onClick={goPrev} disabled={currentMonthIdx >= months.length - 1 && filterMonth !== ""}
              style={{ padding: "7px 9px", borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", display: "flex", color: "#6b7280" }}>
              <ChevronLeft size={15} />
            </button>
            <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} style={{
              padding: "7px 12px", border: "1.5px solid #e5e7eb", borderRadius: 9,
              fontSize: 13, fontWeight: 600, color: "#111827", background: "#fff", cursor: "pointer",
            }}>
              <option value="">All Time</option>
              {months.map((m) => (
                <option key={m} value={m}>
                  {new Date(m + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
                </option>
              ))}
            </select>
            <button onClick={goNext} disabled={filterMonth === ""}
              style={{ padding: "7px 9px", borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", display: "flex", color: "#6b7280" }}>
              <ChevronRight size={15} />
            </button>
          </div>

          {/* Actions */}
          <button onClick={fetchRecords} style={{
            padding: "8px 12px", borderRadius: 8, border: "1.5px solid #e5e7eb",
            background: "#fff", color: "#6b7280", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 5, fontSize: 13,
          }}>
            <RefreshCw size={13} />
          </button>
          <button onClick={exportCSV} disabled={!filtered.length} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
            background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff",
            border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700,
            cursor: filtered.length ? "pointer" : "not-allowed", opacity: filtered.length ? 1 : 0.5,
          }}>
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 20 }}>
        {/* Attendance Rate */}
        <div style={{
          background: "#fff", borderRadius: 14, padding: "18px 20px",
          border: `1.5px solid ${pctBg}`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)", overflow: "hidden", position: "relative",
        }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: pctColor }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: pctBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <TrendingUp size={20} color={pctColor} />
            </div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 800, color: pctColor, lineHeight: 1 }}>{pct}%</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginTop: 3 }}>Attendance Rate</div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>{present} of {total} days</div>
            </div>
          </div>
          {total > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ height: 6, borderRadius: 4, background: "#f1f5f9", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${pct}%`, borderRadius: 4, background: pctColor, transition: "width 0.6s ease" }} />
              </div>
              {pct < 75 && (
                <div style={{ fontSize: 11, color: "#dc2626", marginTop: 5, fontWeight: 600 }}>
                  Below 75% — attend more classes
                </div>
              )}
            </div>
          )}
        </div>

        {/* Present */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "18px 20px", border: "1.5px solid #bbf7d0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "#16a34a" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <CheckCircle size={20} color="#16a34a" />
            </div>
            <div>
              <div style={{ fontSize: 32, fontWeight: 800, color: "#16a34a", lineHeight: 1 }}>{present}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginTop: 3 }}>Present</div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>days attended</div>
            </div>
          </div>
        </div>

        {/* Absent */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "18px 20px", border: "1.5px solid #fecaca", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "#dc2626" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <XCircle size={20} color="#dc2626" />
            </div>
            <div>
              <div style={{ fontSize: 32, fontWeight: 800, color: "#dc2626", lineHeight: 1 }}>{absent}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginTop: 3 }}>Absent</div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>days missed</div>
            </div>
          </div>
        </div>

        {/* Late */}
        <div style={{ background: "#fff", borderRadius: 14, padding: "18px 20px", border: "1.5px solid #fde68a", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "#d97706" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Clock size={20} color="#d97706" />
            </div>
            <div>
              <div style={{ fontSize: 32, fontWeight: 800, color: "#d97706", lineHeight: 1 }}>{late}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginTop: 3 }}>Late</div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>days late</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Records Table ── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", overflow: "hidden" }}>
        {/* Table header */}
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#06b6d4)" }} />
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Calendar size={15} color="#4f46e5" />
          </div>
          <span style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>
            Attendance Records — <span style={{ color: "#4f46e5" }}>{monthLabel}</span>
          </span>
          <span style={{ marginLeft: "auto", fontSize: 12, color: "#9ca3af", background: "#f3f4f6", padding: "3px 10px", borderRadius: 20, fontWeight: 600 }}>
            {filtered.length} record{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {loading ? (
          <div style={{ padding: "56px 0", textAlign: "center" }}>
            <div className="ms2-spin" style={{ width: 28, height: 28, border: "3px solid #e5e7eb", borderTopColor: "#4f46e5", borderRadius: "50%", margin: "0 auto 12px" }} />
            <div style={{ color: "#9ca3af", fontSize: 13 }}>Loading attendance records…</div>
          </div>
        ) : error ? (
          <div style={{ padding: "56px 20px", textAlign: "center" }}>
            <XCircle size={40} color="#fca5a5" style={{ margin: "0 auto 12px", display: "block" }} />
            <div style={{ color: "#dc2626", fontSize: 14, fontWeight: 600 }}>{error}</div>
            <button onClick={fetchRecords} style={{
              marginTop: 14, padding: "8px 20px", borderRadius: 9, border: "none",
              background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff",
              fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}>Retry</button>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "56px 20px", textAlign: "center" }}>
            <BookOpen size={44} color="#e5e7eb" style={{ margin: "0 auto 12px", display: "block" }} />
            <div style={{ color: "#374151", fontSize: 15, fontWeight: 700, marginBottom: 4 }}>No records found</div>
            <div style={{ color: "#9ca3af", fontSize: 13 }}>
              {filterMonth ? "No attendance recorded this month." : "No attendance records available yet."}
            </div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Date", "Day", "Class", "Subject", "Status", "Marked By"].map((h) => (
                    <th key={h} style={{
                      padding: "10px 16px", textAlign: "left", fontWeight: 700,
                      color: "#6b7280", fontSize: 11, textTransform: "uppercase",
                      letterSpacing: "0.06em", borderBottom: "1.5px solid #e5e7eb",
                      whiteSpace: "nowrap",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const cfg  = STATUS_CFG[r.status] || STATUS_CFG.absent;
                  const Icon = cfg.icon;
                  return (
                    <tr key={r.id || i} style={{
                      borderBottom: "1px solid #f1f5f9",
                      background: i % 2 === 0 ? "#fff" : "#fafafa",
                      transition: "background 0.15s",
                    }}>
                      <td style={{ padding: "12px 16px", color: "#111827", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {fmt(r.date)}
                      </td>
                      <td style={{ padding: "12px 16px", color: "#6b7280", fontSize: 12 }}>
                        {dayOfWeek(r.date)}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        {r.class_name
                          ? <span style={{ background: "#eff6ff", color: "#4f46e5", fontSize: 12, fontWeight: 600, padding: "3px 9px", borderRadius: 6 }}>{r.class_name}</span>
                          : <span style={{ color: "#9ca3af" }}>—</span>}
                      </td>
                      <td style={{ padding: "12px 16px", color: "#374151" }}>
                        {r.subject_name || <span style={{ color: "#9ca3af" }}>—</span>}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          padding: "4px 12px", borderRadius: 20,
                          background: cfg.bg, color: cfg.color,
                          fontWeight: 700, fontSize: 12,
                          border: `1px solid ${cfg.border}`,
                        }}>
                          <Icon size={12} /> {cfg.label}
                        </span>
                      </td>
                      <td style={{ padding: "12px 16px", color: "#6b7280" }}>
                        {r.marked_by_name || <span style={{ color: "#9ca3af" }}>Auto</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
